import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiX, FiEye, FiPackage, FiShield, FiTarget, FiDollarSign, FiMapPin, FiCalendar, FiBarChart, FiCopy, FiExternalLink, FiLock } from 'react-icons/fi';
import { useAssetClass } from '../../contexts/AssetClassContext';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import toast from 'react-hot-toast';
import { formatContractDeployedAt } from './AssetClassContractCardBlock';
import { useClientConfig } from '../../contexts/ClientConfigContext';
import { assetClassesApi } from '../../api/smartTokenizerApi';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const Modal = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a3e 0%, #2d2d5f 100%);
  border-radius: 16px;
  width: 100%;
  max-width: 1200px;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
`;

const Header = styled.div`
  padding: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;

  .class-info {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .class-icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: #fff;
  }

  .class-details {
    h2 {
      color: #fff;
      font-size: 1.8rem;
      font-weight: 700;
      margin: 0 0 0.25rem 0;
    }

    .class-type {
      color: #4facfe;
      font-size: 1rem;
      margin-bottom: 0.5rem;
    }

    .class-status {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  }

  .close-btn {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 8px;
    padding: 0.75rem;
    color: #fff;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
  border: 1px solid rgba(34, 197, 94, 0.3);
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }
`;

const TabNavigation = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 2rem;

  .tab {
    padding: 1rem 1.5rem;
    cursor: pointer;
    color: #a0a0a0;
    border-bottom: 2px solid transparent;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:hover {
      color: #fff;
    }

    &.active {
      color: #4facfe;
      border-bottom-color: #4facfe;
    }
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;

  .card-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .icon {
      color: #4facfe;
    }
  }

  .info-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
    font-size: 0.9rem;

    &:last-child {
      margin-bottom: 0;
    }

    .label {
      color: #a0a0a0;
    }

    .value {
      color: #fff;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  }
`;

const TokenList = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
`;

const TokenListHeader = styled.div`
  background: rgba(79, 172, 254, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1rem 1.5rem;
  
  .header-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .header-subtitle {
    color: #a0a0a0;
    font-size: 0.9rem;
    margin-top: 0.25rem;
  }
`;

const TokenTable = styled.div`
  .table-header {
    display: grid;
    grid-template-columns: 1fr 0.9fr minmax(5.5rem, 0.75fr) 1fr 1fr 0.85fr auto;
    gap: 1rem;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    font-size: 0.9rem;
    font-weight: 600;
    color: #a0a0a0;
  }

  .table-row {
    display: grid;
    grid-template-columns: 1fr 0.9fr minmax(5.5rem, 0.75fr) 1fr 1fr 0.85fr auto;
    gap: 1rem;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    font-size: 0.9rem;
    color: #fff;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.02);
    }

    &:last-child {
      border-bottom: none;
    }

    .token-id {
      font-weight: 600;
      color: #4facfe;
    }

    .token-status {
      padding: 0.25rem 0.5rem;
      border-radius: 12px;
      font-size: 0.8rem;
      font-weight: 500;
      text-align: center;

      &.minted {
        background: rgba(34, 197, 94, 0.2);
        color: #22c55e;
      }

      &.transferred {
        background: rgba(79, 172, 254, 0.2);
        color: #4facfe;
      }

      &.burned {
        background: rgba(239, 68, 68, 0.2);
        color: #ef4444;
      }
    }

    .token-actions {
      display: flex;
      gap: 0.5rem;

      button {
        background: rgba(255, 255, 255, 0.1);
        border: none;
        border-radius: 6px;
        padding: 0.25rem;
        color: #a0a0a0;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          background: rgba(79, 172, 254, 0.2);
          color: #4facfe;
        }
      }
    }
  }

  /* Dropdown styling fix */
  select {
    option {
      background: #1a1a3e;
      color: #fff;
    }
  }
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(79, 172, 254, 0.2);
    color: #4facfe;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

/** Avoid `undefined[field]` — that throws before ?. on `.type` can run */
function normalizeRequiredFieldKeys(required) {
  if (required == null) return [];
  if (Array.isArray(required)) {
    return required
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          return item.name || item.field || item.key || item.id;
        }
        return item != null ? String(item) : null;
      })
      .filter(Boolean);
  }
  if (typeof required === 'object') {
    return Object.keys(required);
  }
  return [];
}

function getFieldSpecification(specifications, fieldKey) {
  if (fieldKey == null || specifications == null || typeof specifications !== 'object') {
    return undefined;
  }
  if (specifications[fieldKey] != null) {
    return specifications[fieldKey];
  }
  if (specifications.properties && typeof specifications.properties === 'object') {
    return specifications.properties[fieldKey];
  }
  return undefined;
}

/** Full schema blob may be our `{ required, specifications }` shape or JSON Schema `{ required, properties }`. */
function resolveFieldDefinition(standardSchema, fieldKey) {
  if (fieldKey == null) return undefined;
  return (
    getFieldSpecification(standardSchema?.specifications, fieldKey) ??
    standardSchema?.properties?.[fieldKey]
  );
}

function formatSpecificationSummary(def) {
  if (def == null) return '—';
  if (typeof def === 'string') return def;
  if (typeof def.type === 'string') return def.type;
  if (Array.isArray(def.type)) return def.type.join(' | ');
  if (typeof def === 'object') return 'object';
  return String(def);
}

const AssetClassDetailsModal = ({ assetClass, onClose }) => {
  const { suspendAssetClass, ASSET_CLASS_TYPES } = useAssetClass();
  const { isAdmin } = useTokenIssuer();
  const { addressExplorerUrl, tokenExplorerUrl, transactionExplorerUrl, explorerConfigured } = useClientConfig();
  const [activeTab, setActiveTab] = useState('overview');
  const [issuedTokens, setIssuedTokens] = useState([]);
  const [issuedTokensLoading, setIssuedTokensLoading] = useState(true);
  const [issuedTokensError, setIssuedTokensError] = useState('');
  useEffect(() => {
    if (!assetClass?.id) {
      setIssuedTokens([]);
      setIssuedTokensLoading(false);
      setIssuedTokensError('');
      return undefined;
    }
    let cancelled = false;
    setIssuedTokensLoading(true);
    setIssuedTokensError('');
    (async () => {
      try {
        const { data } = await assetClassesApi.listIssuedTokens(assetClass.id);
        if (cancelled) {
          return;
        }
        const rows = Array.isArray(data) ? data.map(mapIssuedTokenDto) : [];
        rows.sort(compareIssuedTokenRows);
        setIssuedTokens(rows);
      } catch (error) {
        if (!cancelled) {
          setIssuedTokens([]);
          setIssuedTokensError(
            error.response?.data?.message || error.message || 'Failed to load issued tokens'
          );
        }
      } finally {
        if (!cancelled) {
          setIssuedTokensLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assetClass?.id]);

  if (!assetClass) return null;

  const classTypeLabel =
    Object.values(ASSET_CLASS_TYPES).find((t) => t.id === assetClass.classType)?.name ||
    assetClass.classType ||
    '—';

  const handleSuspendClass = async () => {
    if (!isAdmin) {
      toast.error('Only administrators can suspend an asset class.');
      return;
    }

    if (
      !window.confirm(
        'Suspend this asset class? This calls POST /api/asset-classes/{id}/suspend.'
      )
    ) {
      return;
    }

    try {
      await suspendAssetClass(assetClass.id);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Suspend failed');
    }
  };

  const tokens = issuedTokens;
  const schemaFieldKeys = normalizeRequiredFieldKeys(assetClass.standardSchema?.required);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiEye },
    { id: 'tokens', label: 'Issued Tokens', icon: FiPackage },
    { id: 'analytics', label: 'Analytics', icon: FiBarChart }
  ];

  return (
    <Overlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <Modal
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <Header>
          <div className="class-info">
            <div className="class-icon">
              <FiPackage />
            </div>
            <div className="class-details">
              <h2>{assetClass.name}</h2>
              <div className="class-type">{classTypeLabel}</div>
              <div className="class-status">
                <StatusBadge>{assetClass.isActive ? 'Active' : 'Inactive'}</StatusBadge>
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {isAdmin && (
              <button
                type="button"
                className="close-btn"
                title="Suspend asset class (Admin)"
                onClick={handleSuspendClass}
                style={{ color: '#ffb4b4' }}
              >
                <FiLock />
              </button>
            )}
            <button type="button" className="close-btn" onClick={onClose}>
              <FiX />
            </button>
          </div>
        </Header>

        <Content>
          <TabNavigation>
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <div
                  key={tab.id}
                  className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon />
                  {tab.label}
                </div>
              );
            })}
          </TabNavigation>

          {activeTab === 'overview' && (
            <InfoGrid>
              <InfoCard>
                <div className="card-title">
                  <FiTarget className="icon" />
                  Basic Information
                </div>
                <div className="info-item">
                  <span className="label">Symbol</span>
                  <span className="value">{assetClass.marketSymbol || assetClass.code || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="label">Trading Unit</span>
                  <span className="value">{assetClass.tradingUnit}</span>
                </div>
                <div className="info-item">
                  <span className="label">Total Batches</span>
                  <span className="value">{assetClass.totalBatches || 0}</span>
                </div>
                <div className="info-item">
                  <span className="label">Total Supply</span>
                  <span className="value">{assetClass.totalSupply || 0}</span>
                </div>
              </InfoCard>

              <InfoCard>
                <div className="card-title">
                  <FiShield className="icon" />
                  Contract Details
                </div>
                <div className="info-item">
                  <span className="label">Contract Address</span>
                  <span className="value">
                    {assetClass.contractAddress ? (
                      <>
                        {assetClass.contractAddress.slice(0, 10)}…
                        <CopyButton onClick={() => copyToClipboard(assetClass.contractAddress)}>
                          <FiCopy />
                        </CopyButton>
                        <CopyButton
                          disabled={!explorerConfigured}
                          title={explorerConfigured ? 'Block explorer' : 'Block explorer URL not loaded from API'}
                          onClick={() => {
                            const url = addressExplorerUrl(assetClass.contractAddress);
                            if (url) window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          <FiExternalLink />
                        </CopyButton>
                      </>
                    ) : (
                      'Not deployed'
                    )}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Token Standard</span>
                  <span className="value">{assetClass.tokenStandard}</span>
                </div>
                <div className="info-item">
                  <span className="label">Deployment Date</span>
                  <span className="value">
                    {assetClass.contractDeployedAtUtc
                      ? formatContractDeployedAt(assetClass.contractDeployedAtUtc)
                      : new Date(assetClass.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Last Updated</span>
                  <span className="value">
                    {new Date(assetClass.lastUpdated || assetClass.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </InfoCard>

              <InfoCard>
                <div className="card-title">
                  <FiPackage className="icon" />
                  Schema Information
                </div>
                {schemaFieldKeys.length === 0 ? (
                  <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                    No required fields listed in metadata schema (or schema uses an unsupported shape).
                  </div>
                ) : (
                  schemaFieldKeys.map((fieldKey) => {
                    const def = resolveFieldDefinition(assetClass.standardSchema, fieldKey);
                    return (
                      <div key={fieldKey} className="info-item">
                        <span className="label">{fieldKey}</span>
                        <span className="value">{formatSpecificationSummary(def)}</span>
                      </div>
                    );
                  })
                )}
              </InfoCard>

              <InfoCard>
                <div className="card-title">
                  <FiDollarSign className="icon" />
                  Trading Information
                </div>
                <div className="info-item">
                  <span className="label">Batchable</span>
                  <span className="value">{assetClass.batchable ? 'Yes' : 'No'}</span>
                </div>
                <div className="info-item">
                  <span className="label">Trading Mode</span>
                  <span className="value">{assetClass.tradingMode}</span>
                </div>
                <div className="info-item">
                  <span className="label">Market Active</span>
                  <span className="value">{assetClass.isActive ? 'Yes' : 'No'}</span>
                </div>
              </InfoCard>
            </InfoGrid>
          )}

          {activeTab === 'tokens' && (
            <TokenList>
              <TokenListHeader>
                <div className="header-title">
                  <FiPackage />
                  Issued Tokens ({issuedTokensLoading ? '…' : tokens.length})
                </div>
                <div className="header-subtitle">
                  All tokens minted for this asset class (from on-chain mint records)
                </div>
              </TokenListHeader>

              {issuedTokensError && (
                <div style={{ padding: '0 0 1rem', color: '#ffb4b4', fontSize: '0.9rem' }}>
                  {issuedTokensError}
                </div>
              )}

              <TokenTable>
                <div className="table-header">
                  <div>Token ID</div>
                  <div>Asset #</div>
                  <div>Minted qty</div>
                  <div>Owner</div>
                  <div>Location</div>
                  <div>Status</div>
                  <div>Actions</div>
                </div>

                {issuedTokensLoading && (
                  <div style={{ padding: '2rem', textAlign: 'center', color: '#a0a0a0' }}>
                    Loading issued tokens…
                  </div>
                )}

                {!issuedTokensLoading &&
                  tokens.map((token) => (
                    <div key={token.id} className="table-row">
                      <div className="token-id" title={token.tokenId || undefined}>
                        {formatIssuedTokenIdForDisplay(token.tokenId)}
                      </div>
                      <div>{token.assetNumber}</div>
                      <div title="Fungible: base units from mint record; NFT: 1 per token">
                        {formatIssuedTokenMintQuantity(assetClass.tokenStandard, token.mintedAmount)}
                      </div>
                      <div>
                        {token.owner.length > 12
                          ? `${token.owner.slice(0, 6)}...${token.owner.slice(-4)}`
                          : token.owner}
                      </div>
                      <div>{token.location}</div>
                      <div>
                        <span className={`token-status ${token.status.toLowerCase()}`}>
                          {token.status}
                        </span>
                      </div>
                      <div className="token-actions">
                        <button
                          type="button"
                          disabled={!token.tokenId && !token.mintTransactionHash}
                          onClick={() =>
                            copyToClipboard(token.tokenId || token.mintTransactionHash || '')
                          }
                          title={
                            token.tokenId
                              ? 'Copy Token ID'
                              : token.mintTransactionHash
                                ? 'Copy mint transaction hash'
                                : 'Nothing to copy'
                          }
                        >
                          <FiCopy />
                        </button>
                        <button
                          type="button"
                          disabled={
                            !explorerConfigured ||
                            (!token.tokenId && !token.mintTransactionHash)
                          }
                          onClick={() => {
                            if (token.tokenId) {
                              const url = tokenExplorerUrl(assetClass.contractAddress, token.tokenId);
                              if (url) window.open(url, '_blank', 'noopener,noreferrer');
                              return;
                            }
                            const url = transactionExplorerUrl(token.mintTransactionHash);
                            if (url) window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                          title={
                            !explorerConfigured
                              ? 'Block explorer URL not loaded from API'
                              : token.tokenId
                                ? 'View token on explorer'
                                : token.mintTransactionHash
                                  ? 'View mint transaction on explorer'
                                  : 'No token ID or mint transaction hash on this record'
                          }
                        >
                          <FiExternalLink />
                        </button>
                      </div>
                    </div>
                  ))}

                {!issuedTokensLoading && tokens.length === 0 && !issuedTokensError && (
                  <div style={{ padding: '2rem', textAlign: 'center', color: '#a0a0a0' }}>
                    No tokens have been minted for this asset class yet.
                  </div>
                )}
              </TokenTable>
            </TokenList>
          )}

          {activeTab === 'analytics' && (
            <InfoGrid>
              {issuedTokensError && (
                <div style={{ gridColumn: '1 / -1', color: '#ffb4b4', fontSize: '0.9rem' }}>
                  {issuedTokensError}
                </div>
              )}
              <InfoCard>
                <div className="card-title">
                  <FiBarChart className="icon" />
                  Token Distribution
                </div>
                <div className="info-item">
                  <span className="label">Total Minted</span>
                  <span className="value">{issuedTokensLoading ? '…' : tokens.length}</span>
                </div>
                <div className="info-item">
                  <span className="label">Recorded on-chain</span>
                  <span className="value">{issuedTokensLoading ? '…' : tokens.filter((t) => t.status === 'MINTED').length}</span>
                </div>
                <div className="info-item">
                  <span className="label">Transferred</span>
                  <span className="value">{issuedTokensLoading ? '…' : tokens.filter((t) => t.status === 'TRANSFERRED').length}</span>
                </div>
                <div className="info-item">
                  <span className="label">Burned</span>
                  <span className="value">{issuedTokensLoading ? '…' : tokens.filter((t) => t.status === 'BURNED').length}</span>
                </div>
              </InfoCard>

              <InfoCard>
                <div className="card-title">
                  <FiMapPin className="icon" />
                  Geographic Distribution
                </div>
                {issuedTokensLoading ? (
                  <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Loading…</div>
                ) : (
                  getLocationDistribution(tokens).map((location) => (
                    <div key={location.name} className="info-item">
                      <span className="label">{location.name}</span>
                      <span className="value">{location.count} tokens</span>
                    </div>
                  ))
                )}
              </InfoCard>

              <InfoCard>
                <div className="card-title">
                  <FiCalendar className="icon" />
                  Minting Timeline
                </div>
                <div className="info-item">
                  <span className="label">First Mint</span>
                  <span className="value">
                    {issuedTokensLoading
                      ? '…'
                      : tokens.length > 0
                        ? new Date(Math.min(...tokens.map((t) => new Date(t.mintedAt)))).toLocaleDateString()
                        : 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Last Mint</span>
                  <span className="value">
                    {issuedTokensLoading
                      ? '…'
                      : tokens.length > 0
                        ? new Date(Math.max(...tokens.map((t) => new Date(t.mintedAt)))).toLocaleDateString()
                        : 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Avg. per Batch</span>
                  <span className="value">
                    {issuedTokensLoading
                      ? '…'
                      : Number(assetClass.totalBatches) > 0
                        ? Math.round(tokens.length / Number(assetClass.totalBatches))
                        : '—'}
                  </span>
                </div>
              </InfoCard>
            </InfoGrid>
          )}
        </Content>
      </Modal>
    </Overlay>
  );
};

function pickIssuedTokenIdFromRow(row) {
  if (row == null || typeof row !== 'object') return '';
  const v =
    row.tokenId ??
    row.TokenId ??
    row.tokenID ??
    row.TokenID ??
    row.token_id ??
    row.Token_Id;
  if (v == null) return '';
  const s = String(v).trim();
  return s;
}

function formatIssuedTokenIdForDisplay(tokenId) {
  const s = String(tokenId ?? '').trim();
  return s || '—';
}

function mapIssuedTokenDto(row) {
  const tokenId = pickIssuedTokenIdFromRow(row);
  const assetId = row.assetId ?? row.AssetId;
  const mintRaw = row.mintedAtUtc ?? row.MintedAtUtc;
  return {
    id: `${assetId}:${tokenId || 'mint'}`,
    tokenId,
    assetId,
    assetNumber: String(row.assetNumber ?? row.AssetNumber ?? '').trim() || '—',
    mintedAmount: String(row.mintedAmount ?? row.MintedAmount ?? '').trim(),
    mintTransactionHash: String(
      row.mintTransactionHash ?? row.MintTransactionHash ?? ''
    ).trim(),
    owner: String(row.currentOwnerWallet ?? row.CurrentOwnerWallet ?? '').trim() || '—',
    location: String(row.country ?? row.Country ?? '').trim() || 'Unknown',
    status: 'MINTED',
    mintedAt: mintRaw ? new Date(mintRaw).toISOString() : new Date().toISOString()
  };
}

/** Display minted quantity: fungible uses stored base units; ERC-721 shows 1 per row when amount is empty. */
function formatIssuedTokenMintQuantity(tokenStandard, mintedAmountRaw) {
  const raw = String(mintedAmountRaw ?? '').trim();
  if (raw) return raw;
  const ts = tokenStandard;
  if (typeof ts === 'number') {
    if (ts === 1) return '1';
    return '—';
  }
  const std = String(ts ?? '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase();
  if (std === 'ERC721' || String(ts).trim() === '1') return '1';
  return '—';
}

function compareIssuedTokenRows(a, b) {
  const na = Number.parseInt(a.tokenId, 10);
  const nb = Number.parseInt(b.tokenId, 10);
  if (Number.isFinite(na) && Number.isFinite(nb) && String(na) === a.tokenId && String(nb) === b.tokenId) {
    return na - nb;
  }
  return a.tokenId.localeCompare(b.tokenId, undefined, { numeric: true, sensitivity: 'base' });
}

// Helper function to get location distribution
function getLocationDistribution(tokens) {
  const distribution = {};
  tokens.forEach(token => {
    distribution[token.location] = (distribution[token.location] || 0) + 1;
  });
  
  return Object.entries(distribution).map(([name, count]) => ({ name, count }));
}

export default AssetClassDetailsModal; 